import { useTranslation } from "@repo/i18n";
import { Button, Skeleton } from "@repo/components";
import { EmptyState } from "@repo/blocks";
import { css } from "@repo/styled-system/css";
import { useState } from "react";
import { PageScaffold } from "../shell/PageScaffold";
import { useVenue } from "../shell/VenueContext";
import { useServices } from "../bookings/queries";
import { useWaitlist, useWaitlistMutation } from "./queries";
import { guestLabel, waitlistApi, type WaitlistEntry, type WaitlistQueue } from "./waitlistApi";

const QUEUES: WaitlistQueue[] = ["RESERVATION_WAITLIST", "WALKIN_STANDBY"];

// --- Shared card + table styling, matching the polished sibling pages (bookings ListView / FailedCharges).
const card = css({
  borderWidth: "1px",
  borderColor: "border.default",
  rounded: "lg",
  bg: "bg.default",
  overflow: "hidden",
});
const cardHead = css({
  display: "flex",
  alignItems: "center",
  gap: "3",
  flexWrap: "wrap",
  px: "4",
  py: "3",
  borderBottomWidth: "1px",
  borderColor: "border.subtle",
  bg: "bg.subtle",
});
const cardTitle = css({ fontSize: "md", fontWeight: "semibold" });
const countPill = css({
  fontSize: "xs",
  fontWeight: "medium",
  color: "fg.muted",
  bg: "bg.muted",
  rounded: "full",
  px: "2",
  py: "0.5",
  minW: "6",
  textAlign: "center",
});
const scroll = css({ overflowX: "auto", width: "full" });
const table = css({ width: "full", borderCollapse: "collapse", fontSize: "sm" });
const th = css({
  textAlign: "left",
  px: "4",
  py: "2.5",
  fontWeight: "medium",
  color: "fg.muted",
  borderBottomWidth: "1px",
  borderColor: "border.subtle",
  whiteSpace: "nowrap",
});
const td = css({
  px: "4",
  py: "3",
  verticalAlign: "middle",
  borderBottomWidth: "1px",
  borderColor: "border.subtle",
});

// Real tap targets for a shared tablet at the host stand.
const control = css({
  minH: "44px",
  px: "3",
  borderWidth: "1px",
  borderColor: "border.default",
  rounded: "md",
  bg: "bg.default",
  fontSize: "sm",
  _focusVisible: { outlineWidth: "2px", outlineStyle: "solid", outlineColor: "border.emphasized" },
});
const fieldLabel = css({
  display: "flex",
  flexDirection: "column",
  gap: "1",
  fontSize: "sm",
  fontWeight: "medium",
  color: "fg.default",
});

// --- Status badge: theme `booking.*` tokens (no more inline hex), meaning carried by LABEL + a shape
// dot, never hue alone (RGAA/WCAG). Literal css() calls so Panda extracts each palette.
const badgeBase = css({
  display: "inline-flex",
  alignItems: "center",
  gap: "1",
  px: "2",
  py: "0.5",
  rounded: "md",
  borderWidth: "1px",
  fontSize: "xs",
  fontWeight: "medium",
  whiteSpace: "nowrap",
});
const stateBadgeClass: Record<WaitlistEntry["state"], string> = {
  WAITING: css({
    bg: "booking.pending.bg",
    color: "booking.pending.fg",
    borderColor: "booking.pending.border",
  }),
  NOTIFIED: css({
    bg: "booking.confirmed.bg",
    color: "booking.confirmed.fg",
    borderColor: "booking.confirmed.border",
  }),
  CHECKED_IN: css({
    bg: "booking.seated.bg",
    color: "booking.seated.fg",
    borderColor: "booking.seated.border",
  }),
  SEATED: css({
    bg: "booking.completed.bg",
    color: "booking.completed.fg",
    borderColor: "booking.completed.border",
  }),
  CANCELLED: css({
    bg: "booking.cancelled.bg",
    color: "booking.cancelled.fg",
    borderColor: "booking.cancelled.border",
  }),
  EXPIRED: css({
    bg: "booking.noShow.bg",
    color: "booking.noShow.fg",
    borderColor: "booking.noShow.border",
  }),
  NO_SHOW: css({
    bg: "booking.noShow.bg",
    color: "booking.noShow.fg",
    borderColor: "booking.noShow.border",
  }),
  DECLINED: css({
    bg: "booking.cancelled.bg",
    color: "booking.cancelled.fg",
    borderColor: "booking.cancelled.border",
  }),
};
const stateDotClass: Record<WaitlistEntry["state"], string> = {
  WAITING: css({ color: "booking.pending.solid" }),
  NOTIFIED: css({ color: "booking.confirmed.solid" }),
  CHECKED_IN: css({ color: "booking.seated.solid" }),
  SEATED: css({ color: "booking.completed.solid" }),
  CANCELLED: css({ color: "booking.cancelled.solid" }),
  EXPIRED: css({ color: "booking.noShow.solid" }),
  NO_SHOW: css({ color: "booking.noShow.solid" }),
  DECLINED: css({ color: "booking.cancelled.solid" }),
};
// The staff-only flags (GU-4) — amber "notification failed" / red "blacklist flag" — also token-driven.
const flagFailed = css({
  bg: "booking.pending.bg",
  color: "booking.pending.fg",
  borderColor: "booking.pending.border",
});
const flagFlagged = css({
  bg: "booking.noShow.bg",
  color: "booking.noShow.fg",
  borderColor: "booking.noShow.border",
});

function StateBadge({ state }: { state: WaitlistEntry["state"] }) {
  const { t } = useTranslation();
  return (
    <span
      className={`${badgeBase} ${stateBadgeClass[state]}`}
      data-testid="waitlist-state"
      data-state={state}
    >
      <span aria-hidden className={`${css({ fontSize: "2xs" })} ${stateDotClass[state]}`}>
        ●
      </span>
      {t(`waitlist.state.${state}`)}
    </span>
  );
}

/**
 * The `/v/:venueId/waitlist` staff surface (features/waiting-queues.md, §C.9, R6) — ONE live view over
 * BOTH queues (reservation waitlist + walk-in standby), keyed by `["venue", venueId, "waitlist"]` so
 * `waitlist.*` SSE events refetch it within ~1-2s (realtime-sync). Each entry offers the state-valid
 * one-tap actions (notify with a freed-covers gate → WL-2, check-in, seat → the claim path, cancel,
 * remove). A blacklist FLAG shows a STAFF-only badge (GU-4 — the diner never learns it); a delivery-
 * failed page shows the "page in person" badge (spec edge case). Door quick-add joins either queue.
 * WAITER-gated; the entitlement-off state renders read-only (WL-6) surfaced by the server's 403.
 */
export function WaitlistView() {
  const { venueId } = useVenue();
  const { t } = useTranslation();
  const query = useWaitlist(venueId);

  const entries = query.data?.entries ?? [];
  const byQueue = (q: WaitlistQueue) =>
    entries.filter((e) => e.queue === q).toSorted((a, b) => a.position - b.position);

  return (
    <PageScaffold title={t("waitlist.title")}>
      <DoorQuickAdd venueId={venueId} />

      {query.isPending && (
        <div className={card} data-testid="waitlist-loading">
          <div className={css({ p: "4" })}>
            <Skeleton height="8" mb="3" />
            <Skeleton height="6" mb="2" />
            <Skeleton height="6" />
          </div>
        </div>
      )}
      {query.isError && (
        <div
          role="alert"
          data-testid="waitlist-error"
          className={css({
            p: "4",
            rounded: "lg",
            borderWidth: "1px",
            borderColor: "border.default",
            color: "fg.error",
            fontSize: "sm",
          })}
        >
          {t("waitlist.loadError")}
        </div>
      )}

      {query.data &&
        QUEUES.map((q) => {
          const rows = byQueue(q);
          return (
            <section
              key={q}
              aria-labelledby={`wl-${q}`}
              className={card}
              data-testid={`waitlist-queue-${q}`}
            >
              <div className={cardHead}>
                <h2 id={`wl-${q}`} className={cardTitle}>
                  {t(`waitlist.queue.${q}`)}
                </h2>
                <span className={countPill}>{rows.length}</span>
              </div>
              {rows.length === 0 ? (
                <div data-testid={`waitlist-empty-${q}`}>
                  <EmptyState
                    icon="⏳"
                    title={t("waitlist.emptyQueue")}
                    description={t("waitlist.emptyBody")}
                  />
                </div>
              ) : (
                <div className={scroll}>
                  <table className={table}>
                    <thead>
                      <tr>
                        <th className={th}>{t("waitlist.col.position")}</th>
                        <th className={th}>{t("waitlist.col.guest")}</th>
                        <th className={th}>{t("waitlist.col.party")}</th>
                        <th className={th}>{t("waitlist.col.state")}</th>
                        <th className={th}>{t("waitlist.col.actions")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((e) => (
                        <EntryRow key={e.id} venueId={venueId} entry={e} />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          );
        })}
    </PageScaffold>
  );
}

function EntryRow({ venueId, entry }: { venueId: string; entry: WaitlistEntry }) {
  const { t } = useTranslation();
  const [covers, setCovers] = useState(entry.partySize);
  const [error, setError] = useState<string | null>(null);
  // Inline confirm replaces the blocking window.confirm() (which also froze browser automation).
  const [confirming, setConfirming] = useState<"cancel" | "remove" | null>(null);

  const run = useWaitlistMutation<() => Promise<unknown>>(venueId, (fn) => fn());
  const act = (fn: () => Promise<unknown>) => {
    setError(null);
    run.mutate(fn, { onError: () => setError(t("waitlist.actionFailed")) });
  };

  return (
    <tr data-testid="waitlist-row">
      <td className={td}>{entry.position}</td>
      <td className={td}>
        <div className={css({ fontWeight: "medium" })}>{guestLabel(entry)}</div>
        <div className={css({ fontSize: "xs", color: "fg.muted" })}>
          {entry.phone ?? entry.email ?? ""}
        </div>
        <div className={css({ display: "flex", gap: "1", flexWrap: "wrap", mt: "1" })}>
          {entry.blacklistFlagged && (
            <span className={`${badgeBase} ${flagFlagged}`}>{t("waitlist.badge.flagged")}</span>
          )}
          {entry.deliveryFailed && (
            <span className={`${badgeBase} ${flagFailed}`}>
              {t("waitlist.badge.deliveryFailed")}
            </span>
          )}
        </div>
      </td>
      <td className={td}>{entry.partySize}</td>
      <td className={td}>
        <StateBadge state={entry.state} />
        {entry.quotedWaitMinutes !== null && (
          <div className={css({ fontSize: "xs", color: "fg.muted", mt: "1" })}>
            {t("waitlist.quotedWait", { minutes: entry.quotedWaitMinutes })}
          </div>
        )}
      </td>
      <td className={td}>
        {confirming ? (
          <div
            className={css({ display: "flex", gap: "2", flexWrap: "wrap", alignItems: "center" })}
          >
            <span className={css({ fontSize: "sm" })}>
              {t(`waitlist.confirm.${confirming}`, { guest: guestLabel(entry) })}
            </span>
            <Button
              size="sm"
              variant="solid"
              disabled={run.isPending}
              onClick={() => {
                const fn =
                  confirming === "cancel"
                    ? () => waitlistApi.cancel(venueId, entry.id, entry.version)
                    : () => waitlistApi.decline(venueId, entry.id, entry.version);
                setConfirming(null);
                act(fn);
              }}
            >
              {t("waitlist.confirm.proceed")}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setConfirming(null)}>
              {t("waitlist.confirm.dismiss")}
            </Button>
          </div>
        ) : (
          <div
            className={css({ display: "flex", gap: "2", flexWrap: "wrap", alignItems: "center" })}
          >
            {entry.state === "WAITING" && (
              <>
                <label className={css({ display: "inline-flex", flexDirection: "column" })}>
                  <span className={css({ srOnly: true })}>{t("waitlist.freedCovers")}</span>
                  <input
                    type="number"
                    min={1}
                    value={covers}
                    onChange={(ev) => setCovers(Number(ev.target.value))}
                    className={`${control} ${css({ width: "20" })}`}
                    aria-label={t("waitlist.freedCovers")}
                  />
                </label>
                <Button
                  size="sm"
                  disabled={run.isPending}
                  onClick={() =>
                    act(() => waitlistApi.notify(venueId, entry.id, entry.version, covers))
                  }
                >
                  {t("waitlist.action.notify")}
                </Button>
              </>
            )}
            {entry.state === "NOTIFIED" && (
              <Button
                size="sm"
                disabled={run.isPending}
                onClick={() => act(() => waitlistApi.checkIn(venueId, entry.id, entry.version))}
              >
                {t("waitlist.action.checkIn")}
              </Button>
            )}
            {(entry.state === "NOTIFIED" || entry.state === "CHECKED_IN") && (
              <Button
                size="sm"
                variant="solid"
                disabled={run.isPending}
                onClick={() => act(() => waitlistApi.seat(venueId, entry.id, entry.version))}
              >
                {t("waitlist.action.seat")}
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              disabled={run.isPending}
              onClick={() => setConfirming("cancel")}
            >
              {t("waitlist.action.cancel")}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={run.isPending}
              onClick={() => setConfirming("remove")}
            >
              {t("waitlist.action.remove")}
            </Button>
          </div>
        )}
        {error && (
          <div role="alert" className={css({ fontSize: "xs", color: "fg.error", mt: "1" })}>
            {error}
          </div>
        )}
      </td>
    </tr>
  );
}

/** Door quick-add: staff drop a guest into either queue with a raw phone (normalized server-side to E.164). */
function DoorQuickAdd({ venueId }: { venueId: string }) {
  const { t } = useTranslation();
  const services = useServices(venueId);
  const [serviceId, setServiceId] = useState("");
  // Default to the first service once loaded, so a host never faces an empty picker (or, before, a raw
  // UUID text box). Mirrors ManualEntryDialog — derived directly during render (the condition becomes
  // false, and stays false, the instant `serviceId` is set, so there is no risk of a render loop).
  const firstService = services.data?.[0];
  if (!serviceId && firstService) setServiceId(firstService.id);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [party, setParty] = useState(2);
  const [queue, setQueue] = useState<WaitlistQueue>("WALKIN_STANDBY");
  const [error, setError] = useState<string | null>(null);

  const add = useWaitlistMutation<void>(venueId, () =>
    waitlistApi.add(venueId, {
      serviceId,
      queue,
      givenName: name || undefined,
      phone: phone || undefined,
      partySize: party,
    }),
  );

  const submit = (ev: React.FormEvent) => {
    ev.preventDefault();
    setError(null);
    add.mutate(undefined, {
      onSuccess: () => {
        setName("");
        setPhone("");
      },
      onError: () => setError(t("waitlist.addFailed")),
    });
  };

  return (
    <section className={card} aria-labelledby="wl-add">
      <div className={cardHead}>
        <h2 id="wl-add" className={cardTitle}>
          {t("waitlist.add.title")}
        </h2>
      </div>
      <form
        onSubmit={submit}
        className={css({
          display: "flex",
          gap: "3",
          flexWrap: "wrap",
          alignItems: "end",
          p: "4",
        })}
      >
        <label className={fieldLabel}>
          {t("waitlist.add.service")}
          <select
            value={serviceId}
            onChange={(e) => setServiceId(e.target.value)}
            required
            className={control}
          >
            {(services.data ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.nameFr}
              </option>
            ))}
          </select>
        </label>
        <label className={fieldLabel}>
          {t("waitlist.add.queue")}
          <select
            value={queue}
            onChange={(e) => setQueue(e.target.value as WaitlistQueue)}
            className={control}
          >
            {QUEUES.map((q) => (
              <option key={q} value={q}>
                {t(`waitlist.queue.${q}`)}
              </option>
            ))}
          </select>
        </label>
        <label className={fieldLabel}>
          {t("waitlist.add.name")}
          <input value={name} onChange={(e) => setName(e.target.value)} className={control} />
        </label>
        <label className={fieldLabel}>
          {t("waitlist.add.phone")}
          <input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="06 12 34 56 78"
            className={control}
          />
        </label>
        <label className={fieldLabel}>
          {t("waitlist.add.party")}
          <input
            type="number"
            min={1}
            value={party}
            onChange={(e) => setParty(Number(e.target.value))}
            className={`${control} ${css({ width: "20" })}`}
          />
        </label>
        <Button type="submit" disabled={add.isPending}>
          {t("waitlist.add.submit")}
        </Button>
        {error && (
          <span role="alert" className={css({ fontSize: "sm", color: "fg.error" })}>
            {error}
          </span>
        )}
      </form>
    </section>
  );
}
